import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, X, UserMinus, UserCircle } from "lucide-react";
import LevelBadge from "@/components/level/LevelBadge";
import type { ActivityPlayer } from "@/hooks/useActivityDetail";

interface ParticipantListProps {
  players: ActivityPlayer[];
  organizerId: string;
  spotsTotal: number;
  spotsFilled: number;
  isOrganizer: boolean;
  onApprove?: (playerId: string) => void;
  onReject?: (playerId: string) => void;
  onRemove?: (playerId: string) => void;
  isApproving?: boolean;
  isRejecting?: boolean;
  isRemoving?: boolean;
}

function getStatusBadge(status: string, isOrganizerPlayer: boolean) {
  if (isOrganizerPlayer) {
    return (
      <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/20">
        Arrangör
      </Badge>
    );
  }
  switch (status) {
    case "joined":
      return (
        <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-700 border-green-200">
          Bekräftad
        </Badge>
      );
    case "pending":
      return (
        <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-700 border-amber-200">
          Väntar
        </Badge>
      );
    case "waitlisted":
      return (
        <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-700 border-blue-200">
          Kölista
        </Badge>
      );
    default:
      return null;
  }
}

function getInitials(name: string | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export default function ParticipantList({
  players,
  organizerId,
  spotsTotal,
  spotsFilled,
  isOrganizer,
  onApprove,
  onReject,
  onRemove,
  isApproving,
  isRejecting,
  isRemoving,
}: ParticipantListProps) {
  const activePlayers = players.filter((p) => p.status === "joined");
  const pendingPlayers = players.filter((p) => p.status === "pending");
  const emptySpots = Math.max(0, spotsTotal - spotsFilled);

  // Sort: organizer first, then by join time
  const sortedActive = [...activePlayers].sort((a, b) => {
    if (a.user_id === organizerId) return -1;
    if (b.user_id === organizerId) return 1;
    return new Date(a.joined_at).getTime() - new Date(b.joined_at).getTime();
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span>Spelare ({spotsFilled}/{spotsTotal})</span>
          {emptySpots > 0 && (
            <span className="text-xs font-normal text-muted-foreground">
              {emptySpots} {emptySpots === 1 ? "plats" : "platser"} kvar
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {/* Active players */}
        {sortedActive.map((player) => {
          const isOrganizerPlayer = player.user_id === organizerId;
          return (
            <div
              key={player.id}
              className="flex items-center gap-3 py-2 border-b last:border-0"
            >
              <Avatar className="h-8 w-8">
                {player.profile?.avatar_url && (
                  <AvatarImage src={player.profile.avatar_url} />
                )}
                <AvatarFallback className="text-xs">
                  {getInitials(player.profile?.full_name)}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {player.profile?.full_name ?? "Okänd"}
                </p>
              </div>

              {player.profile?.level != null && (
                <LevelBadge level={player.profile.level} size="sm" />
              )}

              {getStatusBadge(player.status, isOrganizerPlayer)}

              {/* Organizer action: remove player (not self) */}
              {isOrganizer && !isOrganizerPlayer && onRemove && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  onClick={() => onRemove(player.id)}
                  disabled={isRemoving}
                >
                  <UserMinus className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          );
        })}

        {/* Pending players (visible to organizer) */}
        {isOrganizer && pendingPlayers.length > 0 && (
          <>
            <div className="pt-2 pb-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Väntar på godkännande
              </p>
            </div>
            {pendingPlayers.map((player) => (
              <div
                key={player.id}
                className="flex items-center gap-3 py-2 border-b last:border-0"
              >
                <Avatar className="h-8 w-8">
                  {player.profile?.avatar_url && (
                    <AvatarImage src={player.profile.avatar_url} />
                  )}
                  <AvatarFallback className="text-xs">
                    {getInitials(player.profile?.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {player.profile?.full_name ?? "Okänd"}
                  </p>
                </div>
                {player.profile?.level != null && (
                  <LevelBadge level={player.profile.level} size="sm" />
                )}
                {getStatusBadge("pending", false)}
                {onApprove && onReject && (
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-green-700 hover:bg-green-50"
                      onClick={() => onApprove(player.id)}
                      disabled={isApproving}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-700 hover:bg-red-50"
                      onClick={() => onReject(player.id)}
                      disabled={isRejecting}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {/* Empty spot placeholders */}
        {emptySpots > 0 && (
          <div className="space-y-1 pt-1">
            {Array.from({ length: Math.min(emptySpots, 5) }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="flex items-center gap-3 py-2 opacity-40"
              >
                <div className="h-8 w-8 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                  <UserCircle className="h-4 w-4 text-muted-foreground/50" />
                </div>
                <span className="text-sm text-muted-foreground italic">
                  Ledig plats
                </span>
              </div>
            ))}
            {emptySpots > 5 && (
              <p className="text-xs text-muted-foreground pl-11">
                +{emptySpots - 5} fler lediga platser
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
